import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
//import Sidebar from "./Sidebar";
import CheckupQuiz from "./CheckupQuiz"; // Importiere die CheckupQuiz Komponente

// Annahme: Für Level 4 wird ein neues Modell verwendet, da es um "klare Bilder" geht.
// Diese URLs müssen durch die tatsächlichen Modell- und Metadaten-URLs für Level 4 ersetzt werden.
const MODEL_URL_LEVEL4 = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/model.json"; // ERSETZE DIES DURCH DEINE AKTUELLE URL
const METADATA_URL_LEVEL4 = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/metadata.json"; // ERSETZE DIES DURCH DEINE AKTUELLE URL

const LEVEL4_PASSWORD = "GEHIRN";

type Prediction = {
    label: string;
    confidence: number;
};

type Props = {
    onRestart: () => void;
    onNextLevel: () => void;
    avatarImage: string;
    groupName: string;
    onBack?: () => void; // <-- für Abbrechen im Passwort-Modal
    unlocked?: boolean; // <-- für Passwort-Logik
    unlockLevel?: () => void; // <-- für Passwort-Logik
    initialLevelStep?: number; // <-- Neu: initialLevelStep Prop hinzufügen
};


export default function Level4({ onRestart, onNextLevel, avatarImage, groupName, onBack, unlocked, unlockLevel, initialLevelStep }: Props) {    
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [model, setModel] = useState<tf.LayersModel | null>(null);
    const [labels, setLabels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [videoReady, setVideoReady] = useState(false);

    const [showTestModal, setShowTestModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [currentPredictions, setCurrentPredictions] = useState<Prediction[]>([]);
    const [isTestImageCaptured, setIsTestImageCaptured] = useState(false);
    const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null); // State für das aufgenommene Bild
    const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);

    const [showPasswordModal, setShowPasswordModal] = useState(!unlocked);
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");

    // Phasensteuerung für Level 4
    // 0: Einführungstext 1 (Frame 18)
    // 1: Einführungstext 2 / Testanweisung (Frame 19)
    // 2: "Wusstet ihr schon?" Slide (Basierend auf Screenshot 2025-06-01 at 22.20.55.png)
    // 3: Quiz Intro ("Teste dich" Einführung)
    // 4: Quiz anzeigen (Verwendet CheckupQuiz mit Fragen aus Frame 24 & 28)
    // 5: Abschluss (Frame 53) - Weiter zu Level 5
    const [levelStep, setLevelStep] = useState(initialLevelStep ?? 0);

    // Definition der Quizfragen für CheckupQuiz (aus Frame 24 und 28)
    const quizQuestion1 = {
        question: "Was hilft der KI \"KIKI\" besser beim Lernen?",
        options: [
            { label: "A) Ein klares, helles Bild vom Apfel", value: "A" },
            { label: "B) Ein Bild, das verwackelt ist", value: "B" },
            { label: "C) Ein Bild, das mit einer Plastiktüte gemacht wurde", value: "C" },
        ],
        correctAnswer: "A",
        explanation: "Die KI lernt aus klaren und deutlichen Bildern die präzisen Merkmale und Muster, die für die Erkennung notwendig sind.",
    };

    const quizQuestion2 = {
        question: "Warum kann KIKI ein verschwommenes Bild schlecht erkennen?",
        options: [
            { label: "A) Weil sie verschwommene Bilder überspringt", value: "A" },
            { label: "B) Weil auf dem Bild wichtige Einzelheiten nicht gut zu erkennen sind.", value: "B" },
            { label: "C) Weil sie lieber mit scharfen Linien arbeitet", value: "C" },
        ],
        correctAnswer: "B",
        explanation: "Verschwommene Bilder enthalten nicht genug klare Informationen (Merkmale, Kanten, Texturen), die eine KI für die Klassifizierung benötigt.",
    };

    // Text für den "Wusstet du schon?"-Slide (Kombination aus den ursprünglichen Quizfragen-Texten)
    const knewItText = `
        Die KI sieht nicht wie ein Mensch! Die KI kann nicht raten, was auf einem schlechten Bild ist.
        Sie braucht klare und deutliche Fotos, sonst versteht sie das Bild nicht richtig.
        Menschen können manchmal raten – die KI braucht immer gute Daten.
    `;

    useEffect(() => {
        async function loadModelAndMetadata() {
            setLoading(true);
            try {
                const loadedModel = await tf.loadLayersModel(MODEL_URL_LEVEL4);
                setModel(loadedModel);
                const metadataResponse = await fetch(METADATA_URL_LEVEL4);
                const metadata = await metadataResponse.json();
                setLabels(metadata.labels);
            } catch (err) {
                console.error("Fehler beim Laden des Modells oder der Metadaten in Level 4:", err);
            } finally {
                setLoading(false);
            }
        }
        loadModelAndMetadata();
    }, []);

    useEffect(() => {
        if (!showTestModal) {
            setVideoReady(false);
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
            return;
        }

        async function setupCamera() {
            if (!videoRef.current) return;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play();
                        setVideoReady(true);
                    }
                };
            } catch (err) {
                console.error("Fehler beim Zugriff auf die Kamera in Level 4:", err);
                setVideoReady(false);
            }
        }

        setupCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [showTestModal]);

    const handleTestphotoClick = () => {
        setShowTestModal(true);
        setIsTestImageCaptured(false);
        setCurrentPredictions([]);
        // levelStep bleibt bei 1, da das Modal über dem Hauptinhalt liegt
    };

    const captureAndClassifyTestImage = async () => {
        if (!model || !videoRef.current || !canvasRef.current || !videoReady || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
            console.warn("Kann kein Bild aufnehmen: Modell nicht geladen, Kamera nicht bereit oder Video-Dimensionen 0.");
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        setCapturedImageUrl(canvas.toDataURL('image/png'));

        let imgTensor = tf.browser.fromPixels(canvas);
        imgTensor = tf.image.resizeBilinear(imgTensor, [224, 224]).div(255).expandDims(0);

        const predictionTensor = model.predict(imgTensor) as tf.Tensor;
        const predictionData = await predictionTensor.data();
        tf.dispose([imgTensor, predictionTensor]);

        const predictions = labels.map((label, i) => ({
            label,
            confidence: predictionData[i],
        })).sort((a, b) => b.confidence - a.confidence);

        setCurrentPredictions(predictions);
        setShowTestModal(false);
        setShowAnalyzeModal(true);

        setTimeout(() => {
            setShowAnalyzeModal(false);
            setShowResultModal(true);
            setLevelStep(3);
        }, 1500);
    };

    const handleResultModalClose = () => {
        setShowResultModal(false);
        setLevelStep(2); // Geht zum "Wusstet ihr schon?" Slide
    };

    const handleQuizComplete = (passed: boolean) => {
        setLevelStep(4); // Go to final step after quiz
    };

    // Passwort-Modal
    // Password modal logic
    if (showPasswordModal) {
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "40px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    minWidth: "320px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px"
                }}>
                    <h2 style={{ marginBottom: 10, color: "#333" }}>Passwort eingeben für Level 4</h2>
                    <p style={{ color: "#555", fontSize: "1.1em", textAlign: "center" }}>
                        Gebt hier das Passwort ein, um zu Level 4 zu gelangen:
                    </p>
                    <input
                        type="password"
                        value={passwordInput}
                        onChange={e => { setPasswordInput(e.target.value); setPasswordError(""); }}
                        style={{
                            padding: "10px",
                            fontSize: "1.2em",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                            width: "200px",
                            marginBottom: "10px"
                        }}
                        autoFocus
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                if (passwordInput.trim().toUpperCase() === LEVEL4_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    
                                    setPasswordError("Falsches Passwort");
                                }
                            }
                        }}
                    />
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "15px"
                    }}>
                        <button
                            onClick={onBack}
                            style={{
                                backgroundColor: "#f0f0f0",
                                color: "#555",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={() => {
                                if (passwordInput.trim().toUpperCase() === LEVEL4_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }}
                            style={{
                                backgroundColor: "#4c84ff",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Bestätigen
                        </button>
                    </div>
                    {passwordError && (
                        <div style={{ color: "red", fontWeight: 600 }}>{passwordError}</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            fontFamily: 'Nunito, sans-serif',
            backgroundColor: '#f7f9fc',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '30px',
            gap: '30px',
        }}>
            <div style={{
                flexGrow: 1,
                backgroundColor: '#fff',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative', // Needed for absolute positioning of the button
            }}>
                {/* Restart Button top right INSIDE the white card, styled like Level 1 */}
                <button
                    onClick={onRestart}
                    style={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        zIndex: 10,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    🔄 Level neu starten
                </button>

                {/* Header-Bereich mit Avatar und Level-Titel */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid #4c84ff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        marginRight: '20px',
                        flexShrink: 0,
                    }}>
                        <img src={avatarImage} alt="Team Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                        <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '10px' }}>
                            Level 4: Sieht sie nichts, lernt sie nichts
                        </h2>
                        <p style={{ fontSize: '1.4em', color: '#555' }}>
                            KIs brauchen klare Bilder, um gut zu lernen.
                        </p>
                    </div>
                </div>

                {/* Phasenabhängiger Inhalt */}
                {levelStep === 0 && ( /* Frame 18: Einführungstext 1 */
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Jetzt wird's spannend! Was passiert, wenn die Bilder dunkel oder unscharf sind? Dann habe ich es schwerer, alles richtig zu erkennen. Probiert es aus und seht, wie gut ich klarkomme! 📷
                        </p>
                        <div style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                            <button onClick={() => setLevelStep(1)} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 1 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Überklebt eure Kamera mit etwas Tesafilm. Schaut, ob ich es trotzdem erkennen kann. Diskutiert eure Ergebnisse anschließend in der Klasse.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(0)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={handleTestphotoClick} style={buttonStyle}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 2 && (
                    <div style={{ ...contentBoxStyle }}>
                        <h3 style={{ ...headingStyle, marginBottom: '10px' }}>
                            Wusstet ihr schon?
                        </h3>
                        <p style={{ ...paragraphStyle }}>
                            {knewItText}
                        </p>
                        <div style={buttonRowStyle}>
                            {/* <button
                                onClick={() => setLevelStep(1)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button> */}

                            <button
                                onClick={() => {
                                    setShowResultModal(true);
                             
                                }}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>

                            <button onClick={() => setLevelStep(3)} style={buttonStyle}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {/* {levelStep === 3 && (
                    <div style={{ ...contentBoxStyle }}>
                        <h3 style={{ ...headingStyle, marginBottom: '20px', textAlign: 'center' }}>
                            Testet euer Wissen!
                        </h3>
                        <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                            Bist du bereit, dein Wissen über KI und Datenqualität zu testen? Beantworte die folgenden Fragen!
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(2)}
                                style={{
                                    ...buttonStyle,
                                    backgroundColor: '#f0f0f0',
                                    color: '#555',
                                    border: '1px solid #ccc',
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={() => setLevelStep(4)} style={buttonStyle}>
                                Quiz starten
                            </button>
                        </div>
                    </div>
                )} */}

                {levelStep === 3 && ( /* Quiz anzeigen (Frame 24 & 28 Logik) */
                    <CheckupQuiz
                        question1={quizQuestion1}
                        question2={quizQuestion2}
                        onQuizComplete={handleQuizComplete}
                        onBack={() => setLevelStep(2)} // Go back to previous step
                    />
                )}

                {levelStep === 4 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Super gemacht, Gruppe “{groupName}”! 😊 <br />
                            Ihr habt gelernt, wie wichtig klare Bilder für eine KI sind. Klickt “Weiter”, um zu Level 5 zu gelangen.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(3)}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={onNextLevel} style={{ ...buttonStyle, backgroundColor: '#4caf50' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#45a049'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4caf50'}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}


                {/* Test-Modal (Frame 21) */}
                {showTestModal && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle }}>
                            <h3 style={{ ...headingStyle, marginBottom: '15px', textAlign: 'center' }}>
                                Level 4: Klare Bilder
                            </h3>
                            <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                                Nehmt ein neues Testbild auf!
                            </p>
                            <video
                                ref={videoRef}
                                style={{ ...videoStyle, display: videoReady ? 'block' : 'none' }}
                                autoPlay
                                muted
                                playsInline
                            />
                            {!videoReady && (
                                <div style={{ ...videoPlaceholderStyle }}>
                                    Kamera wird geladen...
                                </div>
                            )}

                            <div style={buttonRowStyle}>
                                <button onClick={() => { setShowTestModal(false); }} style={{ ...buttonStyle, backgroundColor: '#d9534f' }}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#c9302c'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#d9534f'}>
                                    Abbrechen
                                </button>
                                <button onClick={captureAndClassifyTestImage} disabled={!videoReady || loading} style={{
                                    ...buttonStyle,
                                    backgroundColor: '#4c84ff',
                                    cursor: (!videoReady || loading) ? 'not-allowed' : 'pointer',
                                    opacity: (!videoReady || loading) ? 0.6 : 1,
                                }} onMouseOver={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#3b6dd6')} onMouseOut={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#4c84ff')}>
                                    Weiter
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                

                {/* Ergebnis-Modal (Frame 22) */}
                {showResultModal && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle, maxWidth: '700px' }}> {/* Erhöhte maximale Breite */}
                            <h3 style={{ ...headingStyle, textAlign: 'center' }}>
                                Ergebnisse:
                            </h3>
                            {/* Icon basierend auf dem Ergebnis, hier nur ein Platzhalter */}
                            {/* <img src="/path/to/apple_icon.png" alt="Testobjekt" style={{ maxWidth: '150px', marginBottom: '15px' }} /> */}
                            {capturedImageUrl && (
                                <img
                                    src={capturedImageUrl}
                                    alt="Aufgenommenes Bild"
                                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', marginBottom: '15px', border: '2px solid #ddd', borderRadius: '8px' }}
                                />
                            )}
                            {currentPredictions.map((prediction, index) => (
                                <div key={index} style={{ width: '100%', marginBottom: '10px' }}>
                                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '5px' }}>
                                        {prediction.label}: {Math.round(prediction.confidence * 100)}%
                                    </p>
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '5px',
                                        height: '15px',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${prediction.confidence * 100}%`,
                                            height: '100%',
                                            backgroundColor: prediction.label === "Apfel" ? '#FF6347' : '#FFD700',
                                            borderRadius: '5px',
                                        }} />
                                    </div>
                                </div>
                            ))}

                            {/* Warnhinweis */}
                            <p style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                marginTop: '10px',
                                textAlign: 'center',
                            }}>
                                Macht einen Screenshot vom Ergebnis!
                            </p>

                            {/* Footer with two buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '15px',
                marginTop: '10px'
            }}>
                <button
                    onClick={() => {
                        setShowResultModal(false);
                        setCapturedImageUrl(null);
                        setCurrentPredictions([]);
                        setShowTestModal(true);
                        setVideoReady(false);
                    }}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    Nochmal ausprobieren
                </button>
                <button onClick={handleResultModalClose} style={{
                    backgroundColor: '#4c84ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 25px',
                    cursor: 'pointer',
                    fontSize: '1.1em',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.2s ease',
                }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}>
                    Weiter
                </button>
            </div>
        </div>
    </div>
)}

                {showAnalyzeModal && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0, 0, 0, 0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}>
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '15px',
                            padding: '40px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                            maxWidth: '600px',
                            width: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '25px',
                            position: 'relative',
                        }}>
                            <h3 style={{ fontSize: '1.8em', color: '#333', marginBottom: '15px', textAlign: 'center' }}>
                                Analysiere Bild...
                            </h3>
                            <div style={{ width: '80%', height: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#4c84ff',
                                    borderRadius: '5px',
                                    animation: 'loading-bar 1.5s forwards linear',
                                }} />
                            </div>
                            <style>
                                {`
                                @keyframes loading-bar {
                                    0% { width: 0%; }
                                    100% { width: 100%; }
                                }
                                `}
                            </style>
                        </div>
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
        </div>
    );
}

// Gemeinsame Stile (können in einer separaten Datei ausgelagert werden, wenn viele Komponenten sie nutzen)
const contentBoxStyle = {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'flex-start',
    gap: '20px',
    backgroundColor: '#e6f7ff',
    border: '2px solid #91d1ff',
    borderRadius: '10px',
    padding: '25px',
    maxWidth: '700px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    marginTop: '30px',
};

const paragraphStyle = {
    fontSize: '1.3em',
    color: '#555',
    lineHeight: '1.6',
};

const headingStyle = {
    fontSize: '1.8em',
    color: '#333',
};

const buttonStyle = {
    marginTop: '20px',
    backgroundColor: '#4c84ff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 25px',
    cursor: 'pointer',
    fontSize: '1.1em',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.2s ease',
};

const buttonRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: '10px',
    marginTop: '20px',
};

const modalOverlayStyle = {
    position: "fixed" as 'fixed',
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '15px',
    padding: '40px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '700px', // Erhöht für ein größeres Video
    width: '90%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    gap: '25px',
    position: 'relative' as 'relative',
};

const videoStyle = {
    border: "3px solid #ddd",
    borderRadius: '10px',
    maxWidth: '100%',
    maxHeight: '600px', // Erhöht für ein größeres Video
};

const videoPlaceholderStyle = {
    width: '100%',
    height: '450px', // Erhöht für ein größeres Video
    backgroundColor: '#eee',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#888',
};
